import type { GameKind } from '../api';

const KEY_PREFIX = 'gp_game_kind:';
// A game's kind never changes (the backend forbids it). Only a custom pack is kept beyond the tab, for a day: it is
// the one kind that changes what a load does before it renders.
const CUSTOM_PACK_KEPT_MS = 24 * 60 * 60 * 1000;
/** Put in the lab's address by a hand-off whose tab could store nothing, so the next load still knows. */
export const GAME_KIND_PARAM = 'game_kind';

function tabKind(gameId: string): GameKind | null {
  try {
    const stored = window.sessionStorage.getItem(KEY_PREFIX + gameId);
    return stored === 'loot_pack' || stored === 'custom_pack' ? stored : null;
  } catch {
    return null;
  }
}

function browserKnowsCustomPack(gameId: string): boolean {
  try {
    const stored = window.localStorage.getItem(KEY_PREFIX + gameId);
    if (stored === null) return false;
    const entry = JSON.parse(stored) as { kind?: unknown; expiresAt?: unknown } | null;
    if (entry?.kind !== 'custom_pack' || typeof entry.expiresAt !== 'number') return false;
    if (entry.expiresAt > Date.now()) return true;
    window.localStorage.removeItem(KEY_PREFIX + gameId);
    return false;
  } catch {
    return false;
  }
}

function addressNamesCustomPack(gameId: string): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('game_id') === gameId && params.get(GAME_KIND_PARAM) === 'custom_pack';
}

/** What this tab knows of the game: its own answer, else a custom pack the browser learned lately or a hand-off named. */
export function readCachedGameKind(gameId: string): GameKind | null {
  const own = tabKind(gameId);
  if (own !== null) return own;
  return browserKnowsCustomPack(gameId) || addressNamesCustomPack(gameId) ? 'custom_pack' : null;
}

/** Stores the kind for the next loads; says whether this tab's own store kept it (blocked storage keeps nothing). */
export function cacheGameKind(gameId: string, kind: GameKind): boolean {
  let kept = false;
  try {
    window.sessionStorage.setItem(KEY_PREFIX + gameId, kind);
    kept = true;
  } catch {
    // Blocked storage: the caller decides what the next load is told instead.
  }
  try {
    if (kind === 'custom_pack') {
      const entry = { kind, expiresAt: Date.now() + CUSTOM_PACK_KEPT_MS };
      window.localStorage.setItem(KEY_PREFIX + gameId, JSON.stringify(entry));
    } else {
      window.localStorage.removeItem(KEY_PREFIX + gameId);
    }
  } catch {
    // The tab's own copy is enough for this tab.
  }
  return kept;
}
